import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Toast from '../../components/UI/Toast';
import {
    ChartBarIcon,
    UsersIcon,
    BriefcaseIcon,
    ClipboardDocumentListIcon,
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    EyeIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    CalendarIcon,
    DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';

interface CompanyStats {
    totalJobs: number;
    activeJobs: number;
    draftJobs: number;
    totalApplications: number;
    pendingApplications: number;
    approvedApplications: number;
    totalTeamMembers: number;
    totalBudgetSpent: number;
    monthlySpent: number;
    jobViews: number;
    conversionRate: number;
    activeApplications: number;
}

interface JobAnalytics {
    _id: string;
    title: string;
    status: string;
    applications: number;
    views: number;
    createdAt: string;
    budgetSpent: number;
}

interface ApplicationAnalytics {
    _id: string;
    jobTitle: string;
    applicantName: string;
    status: string;
    appliedAt: string;
    amount: number;
}

interface ChartData {
    labels: string[];
    datasets: {
        label: string;
        data: number[];
        backgroundColor: string[];
        borderColor: string[];
    }[];
}

const CompanyAnalytics: React.FC = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.auth);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('30d');
    const [stats, setStats] = useState<CompanyStats>({
        totalJobs: 0,
        activeJobs: 0,
        draftJobs: 0,
        totalApplications: 0,
        pendingApplications: 0,
        approvedApplications: 0,
        totalTeamMembers: 0,
        totalBudgetSpent: 0,
        monthlySpent: 0,
        jobViews: 0,
        conversionRate: 0,
        activeApplications: 0,
    });
    const [jobAnalytics, setJobAnalytics] = useState<JobAnalytics[]>([]);
    const [applicationAnalytics, setApplicationAnalytics] = useState<ApplicationAnalytics[]>([]);
    const [chartData, setChartData] = useState<ChartData | null>(null);

    const fetchAnalyticsData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch company stats
            const statsResponse = await api.get('/companies/dashboard-stats');
            setStats(statsResponse.data.stats);

            // Fetch recent jobs with analytics
            const jobsResponse = await api.get('/companies/jobs/recent');
            setJobAnalytics(jobsResponse.data.jobs);

            // Fetch recent applications
            const applicationsResponse = await api.get('/companies/applications/recent');
            setApplicationAnalytics(applicationsResponse.data.applications);

            // Generate chart data
            generateChartData(statsResponse.data.stats, jobsResponse.data.jobs);

        } catch (error: any) {
            console.error('Error fetching analytics data:', error);
            dispatch(showToast({ message: 'Failed to load analytics data', type: 'error' }));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    const generateChartData = (stats: CompanyStats, jobs: JobAnalytics[]) => {
        const chartData: ChartData = {
            labels: ['Total Jobs', 'Active Jobs', 'Applications', 'Team Members', 'Budget Spent'],
            datasets: [
                {
                    label: 'Current Metrics',
                    data: [
                        stats.totalJobs,
                        stats.activeJobs,
                        stats.totalApplications,
                        stats.totalTeamMembers,
                        Math.round(stats.totalBudgetSpent / 1000) // Convert to thousands
                    ],
                    backgroundColor: [
                        'rgba(59, 130, 246, 0.5)',
                        'rgba(16, 185, 129, 0.5)',
                        'rgba(245, 158, 11, 0.5)',
                        'rgba(139, 92, 246, 0.5)',
                        'rgba(239, 68, 68, 0.5)'
                    ],
                    borderColor: [
                        'rgba(59, 130, 246, 1)',
                        'rgba(16, 185, 129, 1)',
                        'rgba(245, 158, 11, 1)',
                        'rgba(139, 92, 246, 1)',
                        'rgba(239, 68, 68, 1)'
                    ]
                }
            ]
        };
        setChartData(chartData);
    };

    useEffect(() => {
        fetchAnalyticsData();
    }, [fetchAnalyticsData, timeRange]);

    const handleTimeRangeChange = (range: string) => {
        setTimeRange(range);
    };

    const exportAnalytics = () => {
        const data = {
            stats,
            jobAnalytics,
            applicationAnalytics,
            exportDate: new Date().toISOString(),
            timeRange
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `company-analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        dispatch(showToast({ message: 'Analytics data exported successfully', type: 'success' }));
    };

    const StatCard: React.FC<{
        title: string;
        value: string | number;
        icon: React.ComponentType<any>;
        change?: number;
        changeType?: 'increase' | 'decrease' | 'neutral';
        color: string;
    }> = ({ title, value, icon: Icon, change, changeType = 'neutral', color }) => (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
                    {change !== undefined && (
                        <div className="flex items-center mt-2">
                            {changeType === 'increase' ? (
                                <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                            ) : changeType === 'decrease' ? (
                                <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                            ) : (
                                <div className="h-4 w-4" />
                            )}
                            <span className={`text-sm ml-1 ${changeType === 'increase' ? 'text-green-600' :
                                changeType === 'decrease' ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                {change > 0 ? '+' : ''}{change}%
                            </span>
                        </div>
                    )}
                </div>
                <div className={`p-3 rounded-lg ${color}`}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
            </div>
        </div>
    );

    const SimpleChart: React.FC<{ data: ChartData }> = ({ data }) => {
        const maxValue = Math.max(...data.datasets[0].data);

        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Overview</h3>
                <div className="space-y-4">
                    {data.labels.map((label, index) => {
                        const value = data.datasets[0].data[index];
                        const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
                        const backgroundColor = data.datasets[0].backgroundColor[index];
                        const borderColor = data.datasets[0].borderColor[index];

                        return (
                            <div key={label} className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">{label}</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{value}</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full transition-all duration-300"
                                        style={{
                                            width: `${percentage}%`,
                                            backgroundColor: borderColor
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Company Analytics</h1>
                        <p className="text-blue-100 mt-1">
                            Track your company performance and insights
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        {/* Time Range Selector */}
                        <div className="flex items-center space-x-2">
                            <CalendarIcon className="h-5 w-5" />
                            <select
                                value={timeRange}
                                onChange={(e) => handleTimeRangeChange(e.target.value)}
                                className="bg-white/20 border border-white/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                            >
                                <option value="7d">Last 7 days</option>
                                <option value="30d">Last 30 days</option>
                                <option value="90d">Last 90 days</option>
                                <option value="1y">Last year</option>
                            </select>
                        </div>

                        {/* Export Button */}
                        <button
                            onClick={exportAnalytics}
                            className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg px-4 py-2 text-white transition-colors"
                        >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            <span>Export</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Jobs"
                    value={stats.totalJobs}
                    icon={BriefcaseIcon}
                    color="bg-blue-500"
                />
                <StatCard
                    title="Active Jobs"
                    value={stats.activeJobs}
                    icon={CheckCircleIcon}
                    color="bg-green-500"
                />
                <StatCard
                    title="Total Applications"
                    value={stats.totalApplications}
                    icon={ClipboardDocumentListIcon}
                    color="bg-yellow-500"
                />
                <StatCard
                    title="Team Members"
                    value={stats.totalTeamMembers}
                    icon={UsersIcon}
                    color="bg-purple-500"
                />
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Job Views"
                    value={stats.jobViews.toLocaleString()}
                    icon={EyeIcon}
                    color="bg-indigo-500"
                />
                <StatCard
                    title="Conversion Rate"
                    value={`${stats.conversionRate}%`}
                    icon={ChartBarIcon}
                    color="bg-pink-500"
                />
                <StatCard
                    title="Budget Spent"
                    value={`₹${(stats.totalBudgetSpent / 1000).toFixed(0)}K`}
                    icon={CurrencyDollarIcon}
                    color="bg-red-500"
                />
                <StatCard
                    title="Monthly Spent"
                    value={`₹${(stats.monthlySpent / 1000).toFixed(0)}K`}
                    icon={ClockIcon}
                    color="bg-orange-500"
                />
            </div>

            {/* Charts and Visualizations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {chartData && <SimpleChart data={chartData} />}

                {/* Application Status Breakdown */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Application Status</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                                <span className="text-gray-600 dark:text-gray-400">Pending</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">{stats.pendingApplications}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                                <span className="text-gray-600 dark:text-gray-400">Approved</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">{stats.approvedApplications}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                                <span className="text-gray-600 dark:text-gray-400">Active</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">{stats.activeApplications}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Jobs Analytics */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Jobs Performance</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Job Title
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Applications
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Views
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Budget Spent
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {jobAnalytics.map((job) => (
                                <tr key={job._id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {job.title}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${job.status === 'active' ? 'bg-green-100 text-green-800' :
                                            job.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                            {job.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {job.applications}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {job.views}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        ₹{(job.budgetSpent / 1000).toFixed(0)}K
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent Applications */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Applications</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Job Title
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Applicant
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Applied Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Amount
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {applicationAnalytics.map((application) => (
                                <tr key={application._id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {application.jobTitle}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {application.applicantName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${application.status === 'approved' ? 'bg-green-100 text-green-800' :
                                            application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                            {application.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {new Date(application.appliedAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        ₹{(application.amount / 1000).toFixed(0)}K
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Toast />
        </div>
    );
};

export default CompanyAnalytics;